import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ParticipatingEvent,
  PromotionCategory,
  SocietySummary,
  getCategories,
  getSocieties,
  getParticipatingEvent,
  createParticipatingEvent,
  updateParticipatingEvent,
} from '../services/api';
import './AdminPromotions.css';
import './AdminParticipatingEventForm.css';

function societyDetailContent(s: SocietySummary): React.ReactNode {
  const cityState = [s.city, s.state]
    .filter((x) => x != null && String(x).trim() !== '')
    .join(', ');
  const loc = s.location?.trim();
  const pin = s.pincode != null && String(s.pincode).trim() !== '' ? `PIN ${s.pincode}` : null;
  const parts: React.ReactNode[] = [];
  if (cityState) {
    parts.push(
      <span key="cs" className="pe-society-detail-line">
        {cityState}
      </span>
    );
  }
  if (loc) {
    parts.push(
      <span key="loc" className="pe-society-detail-line">
        {loc}
      </span>
    );
  }
  if (pin) {
    parts.push(
      <span key="pin" className="pe-society-pin">
        {pin}
      </span>
    );
  }
  if (parts.length === 0) {
    parts.push(
      <span key="empty" className="pe-society-detail-line" style={{ fontStyle: 'italic', color: '#999' }}>
        No address on file
      </span>
    );
  }
  return <>{parts}</>;
}

const LIST_PATH = '/admin/participating-events';

const AdminParticipatingEventForm: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id: routeId } = useParams<{ id: string }>();
  const isNew = location.pathname.endsWith('/new');
  const eventId = routeId ? parseInt(routeId, 10) : NaN;

  const [categories, setCategories] = useState<PromotionCategory[]>([]);
  const [societies, setSocieties] = useState<SocietySummary[]>([]);
  const [formData, setFormData] = useState<Partial<ParticipatingEvent>>({});
  const [existingEvent, setExistingEvent] = useState<ParticipatingEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setLoadError('');
        const [catRes, socRes] = await Promise.all([getCategories(), getSocieties()]);
        if (cancelled) return;
        setCategories(catRes.data);
        setSocieties(socRes.data);

        if (isNew) {
          setExistingEvent(null);
          setFormData({
            title: '',
            description: '',
            image_url: '',
            event_date: '',
            location: '',
            total_seats: null,
            is_active: 1,
            display_order: 0,
            promotion_category_id: catRes.data.length > 0 ? catRes.data[0].id : null,
            society_ids: [],
            button_color: null,
            subtext: null,
            price: null,
            button_text: null,
            interested_text: null,
            whatsapp_group_link: null,
          });
        } else {
          if (isNaN(eventId)) {
            setLoadError('Invalid event');
            return;
          }
          const evRes = await getParticipatingEvent(eventId);
          if (cancelled) return;
          const event = evRes.data;
          setExistingEvent(event);
          setFormData({
            title: event.title,
            description: event.description ?? '',
            image_url: event.image_url ?? '',
            event_date: event.event_date ? event.event_date.split('T')[0] : '',
            location: event.location ?? '',
            total_seats: event.total_seats,
            is_active: event.is_active,
            display_order: event.display_order,
            promotion_category_id: event.promotion_category_id ?? null,
            society_ids: event.society_ids ?? [],
            button_color: event.button_color ?? null,
            subtext: event.subtext ?? null,
            price: event.price ?? null,
            button_text: event.button_text ?? null,
            interested_text: event.interested_text ?? null,
            whatsapp_group_link: event.whatsapp_group_link ?? null,
          });
        }
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          setLoadError('Failed to load form data');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isNew, eventId]);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!formData.title) {
        alert('Title is required');
        return;
      }
      const payload: Partial<ParticipatingEvent> = {
        ...formData,
        total_seats: formData.total_seats != null && String(formData.total_seats).trim() !== '' ? Number(formData.total_seats) : null,
        event_date: formData.event_date || null,
        price: formData.price || null,
        whatsapp_group_link: formData.whatsapp_group_link || null,
      };

      if (isNew) {
        await createParticipatingEvent(payload);
      } else {
        await updateParticipatingEvent(eventId, payload);
      }
      navigate(LIST_PATH);
    } catch (error) {
      console.error('Error saving event:', error);
      alert('Failed to save event');
    }
  };

  if (loading) {
    return <div className="loading">Loading…</div>;
  }

  if (loadError) {
    return (
      <div className="admin-promotions">
        <Link to={LIST_PATH} className="admin-subpage-back">
          ← Back to Participating Events
        </Link>
        <p className="empty-state">{loadError}</p>
      </div>
    );
  }

  return (
    <div className="admin-promotions admin-participating-form-page">
      <Link to={LIST_PATH} className="admin-subpage-back">
        ← Back to Participating Events
      </Link>

      <div className="select-customer-step">
        <div className="select-customer-box">
          <h3>{isNew ? 'Create Event' : 'Edit Event'}</h3>
          {!isNew && existingEvent && (
            <p style={{ marginTop: 0, color: '#666', fontSize: 14 }}>{existingEvent.title}</p>
          )}
          <form onSubmit={handleFormSubmit} className="pe-form">
            <div className="pe-form-layout">
              <div className="pe-form-main">
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                value={formData.title || ''}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="form-group">
              <label>Image URL</label>
              <input
                type="text"
                value={formData.image_url || ''}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                placeholder="https://..."
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Event Date</label>
                <input
                  type="date"
                  value={formData.event_date || ''}
                  onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Category</label>
                <select
                  value={formData.promotion_category_id ?? ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      promotion_category_id: e.target.value ? Number(e.target.value) : null,
                    })
                  }
                >
                  <option value="">Select category...</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Total Seats</label>
                <input
                  type="number"
                  min={1}
                  value={formData.total_seats ?? ''}
                  onChange={(e) => setFormData({ ...formData, total_seats: e.target.value ? Number(e.target.value) : null })}
                  placeholder="Unlimited if empty"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Location</label>
              <input
                type="text"
                value={formData.location || ''}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Button Color</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="color"
                    value={formData.button_color || '#2d8659'}
                    onChange={(e) => setFormData({ ...formData, button_color: e.target.value })}
                    style={{
                      width: '48px',
                      height: '36px',
                      padding: '2px',
                      cursor: 'pointer',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                    }}
                  />
                  <input
                    type="text"
                    value={formData.button_color || ''}
                    onChange={(e) => setFormData({ ...formData, button_color: e.target.value })}
                    placeholder="#2d8659"
                    style={{ flex: 1 }}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Price</label>
                <input
                  type="text"
                  value={formData.price || ''}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value || null })}
                  placeholder="e.g. ₹499 or Free"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Subtext</label>
              <input
                type="text"
                value={formData.subtext || ''}
                onChange={(e) => setFormData({ ...formData, subtext: e.target.value })}
                placeholder="e.g. Limited spots available!"
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Button Text</label>
                <input
                  type="text"
                  value={formData.button_text || ''}
                  onChange={(e) => setFormData({ ...formData, button_text: e.target.value })}
                  placeholder="e.g. I'm Interested (default)"
                />
              </div>
              <div className="form-group">
                <label>After Interest Text</label>
                <input
                  type="text"
                  value={formData.interested_text || ''}
                  onChange={(e) => setFormData({ ...formData, interested_text: e.target.value })}
                  placeholder="e.g. Received Your Interest (default)"
                />
              </div>
            </div>
            <div className="form-group">
              <label>WhatsApp Group Link</label>
              <input
                type="url"
                value={formData.whatsapp_group_link || ''}
                onChange={(e) => setFormData({ ...formData, whatsapp_group_link: e.target.value })}
                placeholder="e.g. https://chat.whatsapp.com/..."
              />
            </div>
              </div>

              <aside className="pe-form-societies" aria-label="Visible societies">
                <span className="pe-form-societies-title">Visible societies</span>
                <p className="pe-form-societies-hint">
                  Leave &quot;All societies&quot; on, or pick specific societies. The event only appears in the app for
                  residents of selected societies.
                </p>
                <label className="pe-society-all">
                  <input
                    type="checkbox"
                    checked={!formData.society_ids || formData.society_ids.length === 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData({ ...formData, society_ids: [] });
                      }
                    }}
                  />
                  <span>All societies</span>
                </label>
                <div className="pe-society-scroll">
                  <div className="pe-society-cards">
                    {societies.map((s) => {
                      const selected = formData.society_ids?.includes(s.id) ?? false;
                      return (
                        <label
                          key={s.id}
                          className={`pe-society-card${selected ? ' pe-society-card--selected' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={(e) => {
                              const current = formData.society_ids ?? [];
                              if (e.target.checked) {
                                setFormData({
                                  ...formData,
                                  society_ids: [...current, s.id],
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  society_ids: current.filter((sid) => sid !== s.id),
                                });
                              }
                            }}
                          />
                          <div className="pe-society-card-body">
                            <span className="pe-society-name">{s.name}</span>
                            <div className="pe-society-detail">{societyDetailContent(s)}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </aside>
            </div>

            <div className="pe-form-footer">
            <div className="form-row">
              <div className="form-group">
                <label>Display Order</label>
                <input
                  type="number"
                  value={formData.display_order ?? 0}
                  onChange={(e) => setFormData({ ...formData, display_order: Number(e.target.value) })}
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  value={formData.is_active ?? 1}
                  onChange={(e) => setFormData({ ...formData, is_active: Number(e.target.value) })}
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>
            </div>
            <div className="step-actions">
              <Link to={LIST_PATH} className="cancel-button" style={{ textDecoration: 'none', display: 'inline-block' }}>
                Cancel
              </Link>
              <button type="submit" className="add-button">
                {isNew ? 'Create' : 'Update'}
              </button>
            </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminParticipatingEventForm;
